(() => {
  if (customElements.get('croma-image-reveal')) return;

  class CromaImageReveal extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.reveal || this.finished || this.observer) return;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (this.motion.matches || !window.IntersectionObserver || !this.animate) return;
      this.media = this.querySelector('picture');
      if (!this.media) return;
      this.controller = new AbortController();
      this.onMotionChange = () => { if (this.motion.matches) this.revealImmediately(); };
      this.motion.addEventListener('change', this.onMotionChange);
      this.startClip = this.dataset.reveal === 'top' ? 'inset(0 0 100% 0)' : 'inset(100% 0 0 0)';
      this.media.style.clipPath = this.startClip;
      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.play(this.controller.signal);
      }, { threshold: 0.05 });
      this.observer.observe(this);
      // A linked image must never remain clipped when reached by keyboard.
      this.link = this.closest('a');
      this.onFocus = () => this.revealImmediately();
      this.link?.addEventListener('focus', this.onFocus);
    }

    async play(signal) {
      const image = this.querySelector('img');
      try {
        // The frame is in view: load even if the clipped picture delays native lazy loading.
        if (image) image.loading = 'eager';
        if (image?.decode) await image.decode();
        if (signal.aborted || !this.isConnected || this.finished) return;
        const duration = { fast: 500, medium: 900, slow: 1400 }[this.dataset.duration] || 900;
        this.animation = this.media.animate(
          [{ clipPath: this.startClip }, { clipPath: 'inset(0 0 0 0)' }],
          { duration, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'both' }
        );
        await this.animation.finished;
        if (!signal.aborted) this.revealImmediately();
      } catch {
        if (!signal.aborted) this.revealImmediately();
      }
    }

    revealImmediately() {
      this.finished = true;
      this.controller?.abort();
      this.animation?.cancel();
      this.observer?.disconnect();
      this.media?.style.removeProperty('clip-path');
      this.motion?.removeEventListener('change', this.onMotionChange);
      this.link?.removeEventListener('focus', this.onFocus);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.animation?.cancel();
      this.observer?.disconnect();
      this.observer = null;
      this.media?.style.removeProperty('clip-path');
      this.motion?.removeEventListener('change', this.onMotionChange);
      this.link?.removeEventListener('focus', this.onFocus);
    }
  }

  customElements.define('croma-image-reveal', CromaImageReveal);
})();
